import { atom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import { useApplied$ } from '~/features/facets/applied'
import { money } from '../../../lib/money/money'
import { appliedSearchCriteria$ } from './applied'
import { draftSearchCriteria$ } from './draft'
import type { SearchCriteria } from './validate'

const priceRanges = [
  2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500, 8000, 8500, 9000, 9500,
  10000, 11000, 12000, 13000, 14000, 15000, 16000, 17000, 18000, 19000, 20000, 25000, 30000, 35000,
  40000, 45000, 50000,
]

const lowerBoundPrice$ = atom((get) => {
  const useApplied = get(useApplied$)
  return get(useApplied ? appliedSearchCriteria$ : draftSearchCriteria$)?.lowerBoundPrice
})
const upperBoundPrice$ = atom((get) => {
  const useApplied = get(useApplied$)
  return get(useApplied ? appliedSearchCriteria$ : draftSearchCriteria$)?.upperBoundPrice
})

// 上限が選択されていば、それより小さな値しか選択できない
const lowerBoundPriceRanges$ = atom<[label: string, value: string][]>((get) => {
  return createLowerBoundPrices(get(upperBoundPrice$))
})

export function createLowerBoundPrices(
  upperBoundPrice: number | undefined
): [label: string, value: string][] {
  const selectables =
    upperBoundPrice !== undefined ? priceRanges.filter((p) => p < upperBoundPrice) : priceRanges
  return [['下限なし', ''], ...selectables.map((p) => [money(p, '円'), String(p)])] as [
    label: string,
    value: string,
  ][]
}

// 下限が選択されていれば、それより大きな値しか選択できない
const upperBoundPriceRanges$ = atom<[label: string, value: string][]>((get) => {
  return createUpperBoundPrices(get(lowerBoundPrice$))
})

export function createUpperBoundPrices(
  lowerBoundPrice: number | undefined
): [label: string, value: string][] {
  const selectables =
    lowerBoundPrice !== undefined ? priceRanges.filter((p) => p >= lowerBoundPrice) : priceRanges
  return [['上限なし', ''], ...selectables.map((p) => [money(p, '円'), String(p)])] as [
    label: string,
    value: string,
  ][]
}

// select で利用するために string に変換
export function useLowerBoundPriceValue() {
  return useAtomValue(lowerBoundPrice$)?.toString() ?? ''
}

export function useLowerBoundPriceRanges() {
  return useAtomValue(lowerBoundPriceRanges$)
}

export function useSetLowerBoundPrice() {
  const set = useSetAtom(draftSearchCriteria$)
  return useCallback(
    (value: string) => {
      const lowerBoundPrice = value !== '' ? parseInt(value, 10) : undefined
      set((current) => ({ ...current, lowerBoundPrice }))
    },
    [set]
  )
}

// select で利用するために string に変換
export function useUpperBoundPriceValue() {
  return useAtomValue(upperBoundPrice$)?.toString() ?? ''
}

export function useUpperBoundPriceRanges() {
  return useAtomValue(upperBoundPriceRanges$)
}

export function useSetUpperBoundPrice() {
  const set = useSetAtom(draftSearchCriteria$)
  return useCallback(
    (value: string) => {
      const upperBoundPrice = value !== '' ? parseInt(value, 10) : undefined
      set((current) => ({ ...current, upperBoundPrice }))
    },
    [set]
  )
}

export type PriceCriteria = Pick<SearchCriteria, 'lowerBoundPrice' | 'upperBoundPrice'>

export function toPriceLabel(criteria: PriceCriteria): string | undefined {
  const lower = criteria.lowerBoundPrice
  const upper = criteria.upperBoundPrice
  if (lower !== undefined && upper !== undefined) {
    return `${money(lower, '円')}〜${money(upper, '円')}`
  } else if (lower !== undefined) {
    return `${money(lower, '円')}〜`
  } else if (upper !== undefined) {
    return `〜${money(upper, '円')}`
  } else {
    return undefined
  }
}
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('toPriceLabel', () => {
    it('下限のみ', () => {
      expect(toPriceLabel({ lowerBoundPrice: 2000 })).toBe('2,000円〜')
    })

    it('上限のみ', () => {
      expect(toPriceLabel({ upperBoundPrice: 2000 })).toBe('〜2,000円')
    })

    it('下限と上限', () => {
      expect(toPriceLabel({ lowerBoundPrice: 2000, upperBoundPrice: 3000 })).toBe(
        '2,000円〜3,000円'
      )
    })
  })
}
